// Swatch display functionality using parallel page fetching
// This replaces the previous GraphQL approach with a simpler DOM-based solution

document.addEventListener('DOMContentLoaded', function() {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  // Remove any existing swatches without images
  removeSwatchesWithoutImages();

  // Always display first page immediately for better UX
  if (window.productsWithMetafields && window.productsWithMetafields.length > 0) {
    displaySwatches(window.productsWithMetafields);
    // Initialize tabs early so user sees content
    requestAnimationFrame(() => {
      initializeCategoryTabs();
    });
  }

  // Check if there are more pages to fetch
  const hasMorePages = window.nextCollectionPage && window.siblingCollectionHandle;
  const shouldFetchMore = window.siblingCollectionHandle;

  // Fetch additional pages in background without blocking UI
  if (hasMorePages || shouldFetchMore) {
    // Show light transparent loader for background loading
    showLightLoader();
    fetchSiblingCollectionPagesParallel(8); // Increased concurrency for faster loading
  }
});

// Remove swatches without valid images
function removeSwatchesWithoutImages() {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  const swatchItems = container.querySelectorAll('.swatch-item');
  swatchItems.forEach(item => {
    const img = item.querySelector('img');
    if (!img || !img.src || img.src.trim() === '') {
      // Remove items without images
      item.remove();
    } else {
      // Add error handler for images that fail to load
      img.onerror = function() {
        const swatchItem = this.closest('.swatch-item');
        if (swatchItem) {
          swatchItem.remove();
        }
      };
    }
  });
}

// Show loader (full blocking loader - not used for progressive loading)
function showLoader() {
  const loader = document.getElementById("swatch-metaobject-loader");
  const container = document.getElementById("sibling-swatch-container");
  if (loader) {
    loader.style.display = 'flex';
  }
  if (container) {
    container.style.display = 'none';
  }
}

// Hide loader (full blocking loader - not used for progressive loading)
function hideLoader() {
  const loader = document.getElementById("swatch-metaobject-loader");
  const container = document.getElementById("sibling-swatch-container");
  if (loader) {
    loader.style.display = 'none';
  }
  if (container) {
    container.style.display = 'flex';
  }
}

// Show light transparent loader overlay for background loading
function showLightLoader() {
  let loader = document.getElementById("swatch-metaobject-light-loader");
  if (!loader) {
    // Create light loader if it doesn't exist
    const container = document.getElementById("sibling-swatch-container");
    if (!container) return;
    
    // Ensure container has relative positioning for absolute loader
    if (window.getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    
    loader = document.createElement("div");
    loader.id = "swatch-metaobject-light-loader";
    loader.className = "swatch-metaobject-light-loader";
    loader.innerHTML = `
      <div class="swatch-light-loader-content">
        <div class="swatch-light-loader-spinner"></div>
      </div>
    `;
    
    // Insert inside the container so it overlays the swatches
    container.appendChild(loader);
  }
  loader.style.display = 'flex';
}

// Hide light transparent loader
function hideLightLoader() {
  const loader = document.getElementById("swatch-metaobject-light-loader");
  if (loader) {
    loader.style.display = 'none';
  }
}

async function fetchSiblingCollectionPagesParallel(maxConcurrent = 8) {
  const baseUrl = window.location.origin;
  const allProducts = [...(window.productsWithMetafields || [])];
  const nextPage = window.nextCollectionPage;

  // First page already displayed, hide loader if shown
  if (!nextPage) {
    hideLightLoader();
    return;
  }

  const collectionHandle = window.siblingCollectionHandle;
  if (!collectionHandle) {
    hideLightLoader();
    return;
  }

  // Optimized: Start with fewer pages and expand if needed
  // Stop early when we get empty results
  let pageNum = 2;
  let consecutiveEmptyPages = 0;
  const maxConsecutiveEmpty = 2; // Stop after 2 consecutive empty pages
  const maxPages = 20; // Reduced from 50 for faster loading

  async function fetchPage(pageIndex) {
    try {
      const url = `${baseUrl}/collections/${collectionHandle}?page=${pageIndex}`;
      const res = await fetch(url, {
        // Use default cache strategy (browser handles it)
        cache: 'default'
      });
      
      if (!res.ok) return { products: [], hasMore: false };

      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const scripts = [...doc.querySelectorAll("script")].map(s => s.textContent);
      const productScript = scripts.find(t => t.includes("window.productsWithMetafields"));

      if (!productScript) return { products: [], hasMore: false };

      const sandbox = {};
      new Function("window", productScript)(sandbox);

      const products = sandbox.productsWithMetafields || [];
      
      // Check if there's a next page
      const nextPageScript = scripts.find(t => t.includes("window.nextCollectionPage"));
      const hasMore = nextPageScript ? nextPageScript.includes("nextCollectionPage") : false;

      return { products, hasMore };
    } catch (err) {
      return { products: [], hasMore: false };
    }
  }

  // Progressive loading: fetch and display as we go
  try {
    while (pageNum <= maxPages && consecutiveEmptyPages < maxConsecutiveEmpty) {
      // Fetch a batch of pages
      const batch = [];
      for (let i = 0; i < maxConcurrent && pageNum <= maxPages; i++) {
        batch.push(fetchPage(pageNum++));
      }

      if (batch.length === 0) break;

      // Wait for batch to complete
      const results = await Promise.all(batch);
      
      let batchHasProducts = false;
      const newProducts = [];

      results.forEach(result => {
        if (result.products && result.products.length > 0) {
          batchHasProducts = true;
          consecutiveEmptyPages = 0;
          result.products.forEach(p => {
            // Avoid duplicates
            if (!allProducts.find(existing => existing.handle === p.handle)) {
              newProducts.push(p);
              allProducts.push(p);
            }
          });
        } else {
          consecutiveEmptyPages++;
        }
      });

      // Display new products immediately (progressive rendering)
      if (newProducts.length > 0) {
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          displaySwatches(newProducts);
          // Debounce tab initialization to avoid excessive re-renders
          if (window.tabInitTimeout) {
            clearTimeout(window.tabInitTimeout);
          }
          window.tabInitTimeout = setTimeout(() => {
            initializeCategoryTabs();
          }, 150);
        });
      }

      // Stop if we got empty results
      if (!batchHasProducts && consecutiveEmptyPages >= maxConsecutiveEmpty) {
        break;
      }
    }
  } catch (error) {
    console.error('Error fetching swatches:', error);
  } finally {
    // Store all products for reference
    window.allSiblingProductsWithMetafields = allProducts;
    
    // Hide light loader when done (always hide, even on error)
    hideLightLoader();
    
    // Final update to ensure everything is properly initialized
    if (window.tabInitTimeout) {
      clearTimeout(window.tabInitTimeout);
    }
    requestAnimationFrame(() => {
      initializeCategoryTabs();
    });
  }
}

function normalizeCategoryHandle(value) {
  if (!value && value !== 0) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function displaySwatches(products) {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return 0;

  // Initialize global seen handles set if it doesn't exist
  if (!window.seenSwatchHandles) {
    window.seenSwatchHandles = new Set();
  }

  let count = 0;
  const currentProductHandle = window.currentProductHandle;
  const activeTab = document.querySelector('.swatch-category-tab.active');
  const activeCategory = activeTab ? activeTab.getAttribute('data-category') : null;

  // Use DocumentFragment for better performance when adding multiple items
  const fragment = document.createDocumentFragment();

  products.forEach(product => {
    const swatch = product.metafields?.swatch;
    // Skip if swatch doesn't exist, has no image, or has no name
    if (!swatch || !swatch.image || !swatch.name || swatch.image.trim() === '') return;

    // Skip duplicates - check both in Set and in DOM
    if (window.seenSwatchHandles.has(product.handle)) return;
    
    // Also check if element already exists in DOM
    const existingItem = container.querySelector(`[data-product-handle="${product.handle}"]`);
    if (existingItem) {
      window.seenSwatchHandles.add(product.handle);
      return;
    }

    window.seenSwatchHandles.add(product.handle);

    // Check if this is the current product
    const isCurrent = currentProductHandle && product.handle === currentProductHandle;

    // Create swatch item with proper structure matching existing CSS
    const item = document.createElement("div");
    item.className = `swatch-item ${isCurrent ? 'swatch-item--current' : 'swatch-item--sibling'}`;
    
    // Check availability - handle boolean, string, and undefined values
    // Default to available (true) if property is missing to avoid marking all as unavailable
    const isAvailable = product.available !== undefined 
      ? (product.available === true || product.available === 'true' || product.available === 1)
      : true; // Default to available if property is missing
    
    if (!isAvailable) {
      item.classList.add('swatch-item--unavailable');
    }

    const categoryHandle = normalizeCategoryHandle(swatch.category || '');
    item.setAttribute('data-category', categoryHandle);
    item.setAttribute('data-tooltip', swatch.name);
    item.setAttribute('data-product-handle', product.handle);

    // Create link wrapper
    if (!isCurrent) {
      const link = document.createElement("a");
      link.href = `/products/${product.handle}`;
      link.className = "swatch-link";
      link.setAttribute('aria-label', `View ${swatch.name} style`);

      // Create tooltip
      const tooltip = document.createElement("span");
      tooltip.className = "special-text__tooltip";
      const tooltipText = document.createElement("span");
      tooltipText.className = "tooltip-text";
      tooltipText.textContent = swatch.name;
      tooltip.appendChild(tooltipText);
      link.appendChild(tooltip);

      // Create image with optimized loading
      const img = document.createElement("img");
      img.src = swatch.image;
      img.alt = swatch.name;
      img.width = 60;
      img.height = 60;
      img.loading = "lazy";
      img.decoding = "async";
      img.fetchPriority = "low"; // Lower priority for sibling swatches
      
      // Hide swatch if image fails to load
      img.onerror = function() {
        const swatchItem = this.closest('.swatch-item');
        if (swatchItem) {
          swatchItem.style.display = 'none';
          swatchItem.remove();
        }
      };
      
      link.appendChild(img);

      item.appendChild(link);
          } else {
      // Current product - no link, just image (eager load for current product)
      const img = document.createElement("img");
      img.src = swatch.image;
      img.alt = swatch.name;
      img.width = 60;
      img.height = 60;
      img.loading = "eager";
      img.decoding = "async";
      img.fetchPriority = "high"; // Higher priority for current product
      
      // Hide swatch if image fails to load
      img.onerror = function() {
        const swatchItem = this.closest('.swatch-item');
        if (swatchItem) {
          swatchItem.style.display = 'none';
          swatchItem.remove();
        }
      };
      
      item.appendChild(img);

      const hiddenLabel = document.createElement("span");
      hiddenLabel.className = "visually-hidden";
      hiddenLabel.textContent = swatch.name;
      item.appendChild(hiddenLabel);
    }

    // Apply category filter if active
    if (window.activeCategoryFilter) {
      if (categoryHandle === window.activeCategoryFilter) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    }

    fragment.appendChild(item);
    count++;
  });

  // Append all items at once for better performance
  container.appendChild(fragment);

  return count;
}

// Initialize category tabs
function initializeCategoryTabs() {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  const tabsContainer = document.querySelector('[data-swatch-category-tabs]');
  if (!tabsContainer) return;

  // Get all unique categories from displayed swatches (only those with valid images)
  const swatchItems = container.querySelectorAll('.swatch-item');
  const categories = new Set();
  
  swatchItems.forEach(item => {
    // Only include items with valid images
    const img = item.querySelector('img');
    if (!img || !img.src || img.src.trim() === '') {
      return; // Skip items without images
    }
    
    const category = item.getAttribute('data-category');
    if (category) {
      categories.add(category);
    }
  });

  // Only show tabs if there are multiple categories
  if (categories.size <= 1) {
    tabsContainer.style.display = 'none';
    return;
  }

  tabsContainer.style.display = 'flex';
  
  // Clear existing tabs
  tabsContainer.innerHTML = '';

  // Get category names from products
  const categoryNames = {};
  if (window.allSiblingProductsWithMetafields) {
    window.allSiblingProductsWithMetafields.forEach(product => {
        const category = product.metafields?.swatch?.category;
        if (category) {
          const categoryHandle = normalizeCategoryHandle(category);
        if (!categoryNames[categoryHandle]) {
          categoryNames[categoryHandle] = category;
        }
      }
    });
  }

  // Add products from current page
  if (window.productsWithMetafields) {
    window.productsWithMetafields.forEach(product => {
      const category = product.metafields?.swatch?.category;
      if (category) {
        const categoryHandle = normalizeCategoryHandle(category);
        if (!categoryNames[categoryHandle]) {
          categoryNames[categoryHandle] = category;
        }
      }
    });
  }

  // Create tab buttons
  const sortedCategories = Array.from(categories).sort();
  const currentCategory = window.currentSwatchCategory;
  
  sortedCategories.forEach(categoryHandle => {
    const button = document.createElement("button");
    button.type = "button";
    const isActive = currentCategory === categoryHandle;
    button.className = `swatch-category-tab${isActive ? ' active' : ''}`;
    button.setAttribute('data-category', categoryHandle);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.textContent = categoryNames[categoryHandle] || categoryHandle;
    
    button.addEventListener('click', function() {
      filterByCategory(categoryHandle);
    });
    
    tabsContainer.appendChild(button);
  });
  
  // If there's a current category, filter by it initially
  if (currentCategory && sortedCategories.includes(currentCategory)) {
    window.activeCategoryFilter = currentCategory;
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      filterByCategory(currentCategory);
    }, 100);
  } else {
    // No category filter initially - show all swatches and apply show more limit
    window.activeCategoryFilter = null;
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      // Make sure all swatches are visible
      const swatchItems = container.querySelectorAll('.swatch-item');
      swatchItems.forEach(item => {
        // Clear any hidden state
        item.classList.remove('swatch-item--hidden');
        item.style.display = '';
      });
      // Apply show more limit
      initializeShowMoreButtons();
    }, 100);
  }
}

// Filter swatches by category
function filterByCategory(categoryHandle) {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  const swatchItems = container.querySelectorAll('.swatch-item');
  const tabs = document.querySelectorAll('.swatch-category-tab');

  // Store active category for use in displaySwatches
  window.activeCategoryFilter = categoryHandle;

  // Update tab states
  tabs.forEach(tab => {
    const isActive = tab.getAttribute('data-category') === categoryHandle;
    tab.classList.toggle('active', isActive);
    tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  // Filter swatches - first clear hidden state for items in this category, then apply show more limit
  swatchItems.forEach(item => {
    const itemCategory = item.getAttribute('data-category');
    if (itemCategory === categoryHandle) {
      // Clear hidden state and show item - we'll apply show more limit after
      item.classList.remove('swatch-item--hidden');
      item.style.display = '';
          } else {
      // Hide items from other categories
      item.style.display = 'none';
    }
  });

  // Update show more buttons after filtering (this will apply the limit)
  initializeShowMoreButtons();
}

// Show all swatches (when no category filter is active)
function showAllSwatches() {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  const swatchItems = container.querySelectorAll('.swatch-item');
  const tabs = document.querySelectorAll('.swatch-category-tab');

  // Clear active category filter
  window.activeCategoryFilter = null;

  // Remove active state from all tabs
  tabs.forEach(tab => {
    tab.classList.remove('active');
    tab.setAttribute('aria-pressed', 'false');
  });

  // Show all swatches that aren't hidden by show more
  swatchItems.forEach(item => {
    if (!item.classList.contains('swatch-item--hidden')) {
      item.style.display = '';
    }
  });

  // Update show more buttons
  initializeShowMoreButtons();
}

// Initialize show more buttons
function initializeShowMoreButtons() {
  const container = document.getElementById("sibling-swatch-container");
  if (!container) return;

  const actionsContainer = document.querySelector('.swatch-metaobject-actions-container');
  if (!actionsContainer) return;

  // Clear existing buttons
  actionsContainer.innerHTML = '';

  // Get all unique categories
  const swatchItems = container.querySelectorAll('.swatch-item:not(.swatch-item--current)');
  const categories = new Map();

  swatchItems.forEach(item => {
    const category = item.getAttribute('data-category');
    if (category) {
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category).push(item);
    }
  });

  // Create show more button for each category if needed
  const initialLimit = 23; // Same as original
  const activeTab = document.querySelector('.swatch-category-tab.active');
  const activeCategory = activeTab ? activeTab.getAttribute('data-category') : null;

  categories.forEach((items, categoryHandle) => {
    // If there's an active category filter, only show button for that category
    if (activeCategory && categoryHandle !== activeCategory) {
      return;
    }

    // Get all items in this category (excluding current product)
    // First, get all items that belong to this category
    let categoryItems = Array.from(items).filter(item => {
      // Exclude current product
      if (item.classList.contains('swatch-item--current')) {
        return false;
      }
      
      // Exclude items without valid images
      const img = item.querySelector('img');
      if (!img || !img.src || img.src.trim() === '' || (img.complete && img.naturalWidth === 0)) {
        // Hide and remove items without valid images
        item.style.display = 'none';
        return false;
      }
      
      // Must match the category handle
      const itemCategory = item.getAttribute('data-category');
      return itemCategory === categoryHandle;
    });
    
    // Now filter by visibility - only include items that are not hidden by category filter
    categoryItems = categoryItems.filter(item => {
      const computedStyle = window.getComputedStyle(item);
      const itemCategory = item.getAttribute('data-category');
      
      // If there's an active category filter, only include items from that category
      if (activeCategory && itemCategory !== activeCategory) {
        return false;
      }
      
      // Item should be visible (not hidden by category filter)
      // When there's no active category filter, all items should be visible
      return computedStyle.display !== 'none';
    });

    // Count items that should be visible (excluding current product)
    const visibleCount = categoryItems.length;

    if (visibleCount > initialLimit) {
      // Hide items beyond initial limit
      categoryItems.forEach((item, index) => {
        if (index >= initialLimit) {
          item.classList.add('swatch-item--hidden');
          item.style.display = 'none';
                     } else {
          // Make sure items within limit are visible
          item.classList.remove('swatch-item--hidden');
          item.style.display = '';
        }
      });

      // Create show more button
      const actionsDiv = document.createElement("div");
      actionsDiv.className = "swatch-metaobject-actions";
      actionsDiv.setAttribute('data-category', categoryHandle);

      const button = document.createElement("button");
      button.type = "button";
      button.className = "swatch-show-more-btn";
      button.setAttribute('data-swatch-show-more', '');
      button.setAttribute('data-category', categoryHandle);
      button.setAttribute('aria-expanded', 'false');

      const hiddenCount = visibleCount - initialLimit;
      button.innerHTML = `
        <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.1831 0.803345L6.18311 5.80334L1.18311 0.803345L0.183105 1.80334L6.18311 7.80334L12.1831 1.80334L11.1831 0.803345Z" fill="#9E9CA0"/>
        </svg>
        <span class="swatch-show-more-text">Show ${hiddenCount} more</span>
      `;

      button.addEventListener('click', function() {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const activeTab = document.querySelector('.swatch-category-tab.active');
        const isCategoryActive = !activeTab || activeTab.getAttribute('data-category') === categoryHandle;

        // Get all items in this category (excluding current product)
        const categoryItems = Array.from(items).filter(item => {
          if (item.classList.contains('swatch-item--current')) {
            return false;
          }
              const itemCategory = item.getAttribute('data-category');
          return isCategoryActive && itemCategory === categoryHandle;
        });

        if (isExpanded) {
          // Collapse - hide items beyond initial limit
          categoryItems.forEach((item, index) => {
            if (index >= initialLimit) {
              item.classList.add('swatch-item--hidden');
              item.style.display = 'none';
                } else {
              // Make sure items within limit are visible
              item.classList.remove('swatch-item--hidden');
              item.style.display = '';
                }
          });
          button.setAttribute('aria-expanded', 'false');
          button.querySelector('.swatch-show-more-text').textContent = `Show ${hiddenCount} more`;
              } else {
          // Expand - show all items in this category
          categoryItems.forEach(item => {
            item.classList.remove('swatch-item--hidden');
            item.style.display = '';
          });
          button.setAttribute('aria-expanded', 'true');
          button.querySelector('.swatch-show-more-text').textContent = 'Show less';
        }
      });

      actionsDiv.appendChild(button);
      actionsContainer.appendChild(actionsDiv);
    }
  });
}

